import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { api } from '../api/client';
import { useAuth } from '../stores/auth';

interface PricingTableData {
  total: number;
  rows: Array<{
    endpoint: string;
    unitPriceUsd: number;
    unit: string;
    source: 'billing' | 'catalog';
    lastSeenAt: string | null;
    hasBilling: boolean;
    hasCatalog: boolean;
    callsCount?: number;
  }>;
}

interface DashboardData {
  range: { from: string; to: string };
  filters: Record<string, string | undefined>;
  totals: {
    falUsd: number;
    falCalls: number;
    anthropicUsd: number;
    anthropicCalls: number;
    elevenlabsChars: number;
    elevenlabsCalls: number;
    grandTotalUsd: number;
  };
  daily: Array<{ date: string; falUsd: number; anthropicUsd: number; elevenlabsChars: number }>;
  byModel: Array<{
    provider: 'fal' | 'elevenlabs' | 'anthropic';
    model: string;
    totalUsd: number;
    calls: number;
  }>;
  byProject: Array<{
    projectId: string | null;
    projectName: string | null;
    clientName: string | null;
    totalUsd: number;
    calls: number;
  }>;
  byUser: Array<{
    userId: string;
    userEmail: string;
    totalUsd: number;
    calls: number;
  }>;
  recent: Array<{
    id: string;
    createdAt: string;
    provider: 'fal' | 'elevenlabs' | 'anthropic';
    model: string;
    endpoint: string;
    userEmail: string;
    projectName: string | null;
    clientName: string | null;
    costUsd: number;
    falAccount?: string | null;
    billableUnits?: number | null;
  }>;
}

interface FilterOptions {
  projects: Array<{ id: string; name: string; client: string | null }>;
  clients: string[];
  users: Array<{ id: string; email: string }>;
}

interface FalAlerts {
  lowBalance: { active: boolean; balanceUsd: number | null; threshold: number; capturedAt: string | null };
  highMonthlySpend: { active: boolean; spentUsd: number; threshold: number };
}

const RANGES = [
  { label: '7d', days: 7 },
  { label: '30d', days: 30 },
  { label: '90d', days: 90 },
];

const PROVIDER_COLORS: Record<string, string> = {
  fal: '#fbbf24',         // amber — fal.ai
  anthropic: '#60a5fa',   // blue — Claude
  elevenlabs: '#34d399',  // emerald — ElevenLabs
};

const PROVIDER_LABELS: Record<string, string> = {
  fal: '🟡 fal.ai',
  anthropic: '🔵 Anthropic',
  elevenlabs: '🟢 ElevenLabs',
};

function shortDate(iso: string): string {
  const d = new Date(iso + 'T00:00:00Z');
  return d.toLocaleDateString('es-AR', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

function shortModel(model: string): string {
  if (model.startsWith('fal-ai/')) return model.slice(7);
  if (model.startsWith('claude-')) {
    if (model.includes('haiku')) return 'Haiku 4.5';
    if (model.includes('sonnet')) return 'Sonnet 4.6';
    if (model.includes('opus')) return 'Opus 4.7';
    return model;
  }
  return model.slice(0, 30);
}

export function CostsPage() {
  const [days, setDays] = useState(30);
  const [projectId, setProjectId] = useState('');
  const [clientName, setClientName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [provider, setProvider] = useState<'fal' | 'elevenlabs' | 'anthropic' | ''>('');
  const [data, setData] = useState<DashboardData | null>(null);
  const [filters, setFilters] = useState<FilterOptions | null>(null);
  const [loading, setLoading] = useState(true);
  const [alerts, setAlerts] = useState<FalAlerts | null>(null);

  useEffect(() => {
    api.get<FilterOptions>('/costs/filter-options').then(setFilters).catch(() => setFilters(null));
    api.get<FalAlerts>('/admin/fal-billing/alerts').then(setAlerts).catch(() => setAlerts(null));
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    const to = new Date();
    const from = new Date(to);
    from.setDate(from.getDate() - days);
    params.set('from', from.toISOString());
    params.set('to', to.toISOString());
    if (projectId) params.set('projectId', projectId);
    if (clientName) params.set('clientName', clientName);
    if (userEmail) params.set('userEmail', userEmail);
    if (provider) params.set('provider', provider);
    api
      .get<DashboardData>(`/costs/dashboard?${params}`)
      .then(setData)
      .finally(() => setLoading(false));
  }, [days, projectId, clientName, userEmail, provider]);

  const dailyForChart = useMemo(
    () =>
      data?.daily.map((d) => ({
        ...d,
        label: shortDate(d.date),
      })) ?? [],
    [data],
  );

  const topModels = useMemo(
    () =>
      (data?.byModel ?? [])
        .filter((m) => m.totalUsd > 0)
        .slice(0, 10)
        .map((m) => ({
          ...m,
          label: shortModel(m.model),
        })),
    [data],
  );

  return (
    <div className="p-3 sm:p-6 max-w-6xl mx-auto">
      <header className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-2xl font-bold text-slate-100">📊 Gastos</h1>
          <div className="flex items-center gap-3 text-xs">
            <Link to="/billing" className="text-slate-400 hover:text-vision underline">
              💰 Billing fal.ai
            </Link>
            <Link to="/projects" className="text-slate-400 hover:text-vision underline">
              ← proyectos
            </Link>
          </div>
        </div>
        <p className="text-sm text-slate-400">
          Tracking unificado fal.ai + Anthropic + ElevenLabs. Filtros por proyecto, cliente,
          usuario, provider.
        </p>
      </header>

      {/* Banner de alertas de costo fal.ai (saldo bajo / gasto mensual alto) */}
      {alerts && (alerts.lowBalance.active || alerts.highMonthlySpend.active) && (
        <div className="mb-4 rounded-lg border border-red-500/50 bg-red-500/10 p-3 text-sm text-red-200">
          <div className="font-semibold text-red-300 mb-1">⚠️ Alertas de costo fal.ai</div>
          <ul className="space-y-0.5 list-disc list-inside">
            {alerts.lowBalance.active && (
              <li>
                Saldo bajo:{' '}
                <b>US${alerts.lowBalance.balanceUsd?.toFixed(2) ?? '?'}</b> (umbral US$
                {alerts.lowBalance.threshold}). Conviene recargar — los modelos caros
                (video/avatar) se frenan automáticamente sin saldo.
              </li>
            )}
            {alerts.highMonthlySpend.active && (
              <li>
                Gasto fal.ai este mes:{' '}
                <b>US${alerts.highMonthlySpend.spentUsd.toFixed(2)}</b> (supera el umbral de US$
                {alerts.highMonthlySpend.threshold}).
              </li>
            )}
          </ul>
        </div>
      )}

      {/* Filtros — en mobile cada control va en su línea para no apretarse */}
      <section className="bg-slate-900 border border-slate-700 rounded-lg p-3 mb-4 grid grid-cols-1 sm:flex sm:flex-wrap sm:items-center gap-2 text-xs">
        <div className="flex gap-1">
          {RANGES.map((r) => (
            <button
              key={r.days}
              onClick={() => setDays(r.days)}
              className={`px-2.5 py-1 rounded font-medium transition-colors ${
                days === r.days
                  ? 'bg-vision/20 text-vision border border-vision/40'
                  : 'bg-slate-800 text-slate-400 border border-slate-700 hover:text-slate-200'
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
        <select
          value={projectId}
          onChange={(e) => setProjectId(e.target.value)}
          className="bg-slate-950 border border-slate-700 rounded px-2 py-1 text-slate-200"
        >
          <option value="">Todos los proyectos</option>
          {filters?.projects.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
              {p.client ? ` (${p.client})` : ''}
            </option>
          ))}
        </select>
        <select
          value={clientName}
          onChange={(e) => setClientName(e.target.value)}
          className="bg-slate-950 border border-slate-700 rounded px-2 py-1 text-slate-200"
        >
          <option value="">Todos los clientes</option>
          {filters?.clients.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <select
          value={userEmail}
          onChange={(e) => setUserEmail(e.target.value)}
          className="bg-slate-950 border border-slate-700 rounded px-2 py-1 text-slate-200"
        >
          <option value="">Todos los usuarios</option>
          {filters?.users.map((u) => (
            <option key={u.id} value={u.email}>
              {u.email}
            </option>
          ))}
        </select>
        <select
          value={provider}
          onChange={(e) => setProvider(e.target.value as typeof provider)}
          className="bg-slate-950 border border-slate-700 rounded px-2 py-1 text-slate-200"
        >
          <option value="">Todos los providers</option>
          <option value="fal">🟡 fal.ai</option>
          <option value="anthropic">🔵 Anthropic</option>
          <option value="elevenlabs">🟢 ElevenLabs</option>
        </select>
        {(projectId || clientName || userEmail || provider) && (
          <button
            onClick={() => {
              setProjectId('');
              setClientName('');
              setUserEmail('');
              setProvider('');
            }}
            className="text-slate-500 hover:text-slate-300"
          >
            ✕ limpiar
          </button>
        )}
      </section>

      {loading && !data && <div className="text-slate-400 text-sm">Cargando…</div>}

      {data && (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
            <Metric
              label="💰 Total combinado"
              value={`$${data.totals.grandTotalUsd.toFixed(2)}`}
              color="text-amber-400"
            />
            <Metric
              label="🟡 fal.ai"
              value={`$${data.totals.falUsd.toFixed(2)}`}
              sublabel={`${data.totals.falCalls} calls`}
              color="text-yellow-400"
            />
            <Metric
              label="🔵 Anthropic"
              value={`$${data.totals.anthropicUsd.toFixed(2)}`}
              sublabel={`${data.totals.anthropicCalls} calls`}
              color="text-blue-400"
            />
            <Metric
              label="🟢 ElevenLabs"
              value={`${data.totals.elevenlabsChars.toLocaleString()} chars`}
              sublabel={`${data.totals.elevenlabsCalls} calls (plan fijo)`}
              color="text-emerald-400"
            />
          </div>

          {/* Daily area chart */}
          <section className="bg-slate-900 border border-slate-700 rounded-lg p-4 mb-4">
            <h2 className="text-xs uppercase text-slate-400 mb-2">📈 Gasto diario USD (fal + Anthropic)</h2>
            {dailyForChart.length === 0 ? (
              <div className="text-slate-500 text-xs italic py-8 text-center">
                Sin gastos en este rango.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={240}>
                <AreaChart data={dailyForChart}>
                  <defs>
                    <linearGradient id="falGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={PROVIDER_COLORS.fal} stopOpacity={0.6} />
                      <stop offset="100%" stopColor={PROVIDER_COLORS.fal} stopOpacity={0.05} />
                    </linearGradient>
                    <linearGradient id="anthropicGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={PROVIDER_COLORS.anthropic} stopOpacity={0.6} />
                      <stop offset="100%" stopColor={PROVIDER_COLORS.anthropic} stopOpacity={0.05} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="label" stroke="#64748b" fontSize={10} />
                  <YAxis stroke="#64748b" fontSize={10} tickFormatter={(v) => `$${v.toFixed(2)}`} />
                  <Tooltip
                    contentStyle={{
                      background: '#1e293b',
                      border: '1px solid #334155',
                      borderRadius: 8,
                      color: '#e2e8f0',
                      fontSize: 12,
                    }}
                    labelStyle={{ color: '#94a3b8' }}
                    formatter={(value, name) => {
                      const v = Number(value ?? 0);
                      if (name === 'falUsd') return [`$${v.toFixed(4)}`, 'fal.ai'];
                      if (name === 'anthropicUsd') return [`$${v.toFixed(4)}`, 'Anthropic'];
                      return [String(value), String(name)];
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="falUsd"
                    stackId="1"
                    stroke={PROVIDER_COLORS.fal}
                    strokeWidth={2}
                    fill="url(#falGrad)"
                  />
                  <Area
                    type="monotone"
                    dataKey="anthropicUsd"
                    stackId="1"
                    stroke={PROVIDER_COLORS.anthropic}
                    strokeWidth={2}
                    fill="url(#anthropicGrad)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </section>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
            {/* Top modelos */}
            <section className="bg-slate-900 border border-slate-700 rounded-lg p-4">
              <h2 className="text-xs uppercase text-slate-400 mb-2">🏆 Top modelos por gasto</h2>
              {topModels.length === 0 ? (
                <div className="text-slate-500 text-xs italic py-4 text-center">Sin datos.</div>
              ) : (
                <ResponsiveContainer width="100%" height={Math.max(180, topModels.length * 28)}>
                  <BarChart data={topModels} layout="vertical" margin={{ left: 5 }}>
                    <XAxis
                      type="number"
                      stroke="#64748b"
                      fontSize={10}
                      tickFormatter={(v) => `$${v.toFixed(2)}`}
                    />
                    <YAxis
                      type="category"
                      dataKey="label"
                      stroke="#94a3b8"
                      fontSize={10}
                      width={150}
                    />
                    <Tooltip
                      contentStyle={{
                        background: '#1e293b',
                        border: '1px solid #334155',
                        borderRadius: 8,
                        color: '#e2e8f0',
                        fontSize: 12,
                      }}
                      formatter={(value, _, item) => {
                        const v = Number(value ?? 0);
                        const calls = ((item as { payload?: { calls?: number } })?.payload?.calls) ?? 0;
                        return [`$${v.toFixed(4)} (${calls} calls)`, 'Costo'];
                      }}
                    />
                    <Bar dataKey="totalUsd" radius={[0, 4, 4, 0]}>
                      {topModels.map((m, i) => (
                        <Cell key={i} fill={PROVIDER_COLORS[m.provider]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </section>

            {/* Por proyecto */}
            <section className="bg-slate-900 border border-slate-700 rounded-lg p-4">
              <h2 className="text-xs uppercase text-slate-400 mb-2">📁 Por proyecto / cliente</h2>
              {data.byProject.length === 0 ? (
                <div className="text-slate-500 text-xs italic py-4 text-center">Sin datos.</div>
              ) : (
                <div className="overflow-x-auto -mx-4 px-4">
                  <table className="w-full text-xs min-w-[24rem]">
                    <thead className="text-slate-500 uppercase">
                      <tr>
                        <th className="text-left pb-1">Proyecto</th>
                        <th className="text-left pb-1">Cliente</th>
                        <th className="text-right pb-1">Gasto</th>
                        <th className="text-right pb-1">Calls</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.byProject.slice(0, 12).map((p) => (
                        <tr key={p.projectId ?? 'no-project'} className="border-t border-slate-800">
                          <td className="py-1 text-slate-200">
                            {p.projectName ?? <span className="italic text-slate-500">— sin proyecto —</span>}
                          </td>
                          <td className="py-1 text-slate-400">{p.clientName ?? '—'}</td>
                          <td className="py-1 text-right text-amber-400 font-medium whitespace-nowrap">
                            ${p.totalUsd.toFixed(4)}
                          </td>
                          <td className="py-1 text-right text-slate-500">{p.calls}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </div>

          {/* Por usuario */}
          {data.byUser.length > 0 && (
            <section className="bg-slate-900 border border-slate-700 rounded-lg p-4 mb-4">
              <h2 className="text-xs uppercase text-slate-400 mb-2">👤 Por usuario</h2>
              <div className="overflow-x-auto -mx-4 px-4">
                <table className="w-full text-xs min-w-[24rem]">
                  <thead className="text-slate-500 uppercase">
                    <tr>
                      <th className="text-left pb-1">Email</th>
                      <th className="text-right pb-1">Gasto</th>
                      <th className="text-right pb-1">Calls</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.byUser.map((u) => (
                      <tr key={u.userId} className="border-t border-slate-800">
                        <td className="py-1 text-slate-200 truncate max-w-[16rem]">{u.userEmail}</td>
                        <td className="py-1 text-right text-amber-400 font-medium whitespace-nowrap">
                          ${u.totalUsd.toFixed(4)}
                        </td>
                        <td className="py-1 text-right text-slate-500">{u.calls}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {/* Detalle reciente */}
          <section className="bg-slate-900 border border-slate-700 rounded-lg p-4">
            <h2 className="text-xs uppercase text-slate-400 mb-2">
              🕐 Últimos 50 gastos
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-slate-500 uppercase border-b border-slate-800">
                  <tr>
                    <th className="text-left py-1 pr-2">Fecha</th>
                    <th className="text-left py-1 pr-2">Provider</th>
                    <th className="text-left py-1 pr-2">Cuenta</th>
                    <th className="text-left py-1 pr-2">Modelo</th>
                    <th className="text-right py-1 pr-2">Units</th>
                    <th className="text-left py-1 pr-2">Usuario</th>
                    <th className="text-left py-1 pr-2">Proyecto</th>
                    <th className="text-right py-1">Costo</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recent.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="py-4 text-center text-slate-500 italic">
                        Sin gastos en este período.
                      </td>
                    </tr>
                  ) : (
                    data.recent.map((r) => (
                      <tr key={r.id} className="border-b border-slate-800/50 hover:bg-slate-800/30">
                        <td className="py-1 pr-2 text-slate-400 whitespace-nowrap">
                          {new Date(r.createdAt).toLocaleString('es-AR', {
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </td>
                        <td className="py-1 pr-2 text-slate-300">{PROVIDER_LABELS[r.provider]}</td>
                        <td className="py-1 pr-2">
                          {r.falAccount ? (
                            <span
                              className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${
                                r.falAccount === 'cuenta-fox'
                                  ? 'bg-amber-900/40 text-amber-300'
                                  : 'bg-emerald-900/40 text-emerald-300'
                              }`}
                              title={`Cuenta fal.ai: ${r.falAccount}`}
                            >
                              {r.falAccount}
                            </span>
                          ) : (
                            <span className="text-slate-600 text-[10px]">—</span>
                          )}
                        </td>
                        <td className="py-1 pr-2 text-slate-300 font-mono text-[10px]">
                          {shortModel(r.model)}
                        </td>
                        <td className="py-1 pr-2 text-right text-slate-500 font-mono text-[10px] whitespace-nowrap">
                          {r.billableUnits != null ? r.billableUnits : '—'}
                        </td>
                        <td className="py-1 pr-2 text-slate-400">{r.userEmail}</td>
                        <td className="py-1 pr-2 text-slate-400">
                          {r.projectName ?? <span className="italic text-slate-600">—</span>}
                          {r.clientName && (
                            <span className="text-slate-600 text-[10px]"> · {r.clientName}</span>
                          )}
                        </td>
                        <td className="py-1 text-right text-amber-400 font-medium whitespace-nowrap">
                          {r.costUsd > 0 ? `$${r.costUsd.toFixed(4)}` : '—'}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <PricingTableAdmin />
        </>
      )}
    </div>
  );
}

/**
 * Tabla unificada de precios (billing real + catálogo oficial). Solo admin.
 * Sirve para auditar visualmente "cuánto sale X" sin necesitar abrir el chat
 * con un agente.
 */
function PricingTableAdmin() {
  const user = useAuth((s) => s.user);
  const [search, setSearch] = useState('');
  const [sourceFilter, setSourceFilter] = useState<'all' | 'billing' | 'catalog'>('all');
  const [data, setData] = useState<PricingTableData | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user?.role !== 'admin') return;
    setLoading(true);
    const params = new URLSearchParams();
    if (sourceFilter !== 'all') params.set('source', sourceFilter);
    if (search.trim()) params.set('search', search.trim());
    api
      .get<PricingTableData>(`/admin/fal-billing/pricing/table?${params}`)
      .then(setData)
      .finally(() => setLoading(false));
  }, [user, sourceFilter, search]);

  if (user?.role !== 'admin') return null;

  return (
    <section className="bg-slate-900 border border-slate-700 rounded-lg p-4">
      <div className="flex items-start justify-between mb-3 gap-3 flex-wrap">
        <div>
          <h2 className="text-xs uppercase text-slate-400">💰 Tabla de precios fal.ai (admin)</h2>
          <p className="text-[10px] text-slate-500 mt-0.5">
            Billing real (tu cuenta) + catálogo oficial (fal.ai/api/models). {data?.total ?? 0} endpoints.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="text"
            placeholder="Buscar endpoint…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="bg-slate-950 border border-slate-700 rounded px-2 py-1 text-xs text-slate-100 w-48 focus:outline-none focus:border-vision"
          />
          <select
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value as 'all' | 'billing' | 'catalog')}
            className="bg-slate-950 border border-slate-700 rounded px-2 py-1 text-xs text-slate-100"
          >
            <option value="all">Todas las fuentes</option>
            <option value="billing">Solo billing real</option>
            <option value="catalog">Solo catálogo oficial</option>
          </select>
        </div>
      </div>

      <div className="overflow-x-auto max-h-96 overflow-y-auto">
        <table className="w-full text-xs">
          <thead className="text-slate-500 uppercase border-b border-slate-800 sticky top-0 bg-slate-900">
            <tr>
              <th className="text-left py-1 pr-2">Endpoint</th>
              <th className="text-left py-1 pr-2">Fuente</th>
              <th className="text-right py-1 pr-2">Precio</th>
              <th className="text-left py-1 pr-2">Unit</th>
              <th className="text-right py-1 pr-2">Calls</th>
              <th className="text-left py-1">Last seen</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={6} className="py-4 text-center text-slate-500 italic">
                  Cargando…
                </td>
              </tr>
            )}
            {!loading && (data?.rows.length ?? 0) === 0 && (
              <tr>
                <td colSpan={6} className="py-4 text-center text-slate-500 italic">
                  Sin resultados.
                </td>
              </tr>
            )}
            {!loading &&
              data?.rows.map((r) => (
                <tr
                  key={r.endpoint}
                  className="border-b border-slate-800/50 hover:bg-slate-800/30"
                >
                  <td className="py-1 pr-2 text-slate-300 font-mono text-[10px]">
                    {r.endpoint}
                  </td>
                  <td className="py-1 pr-2">
                    <span
                      className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${
                        r.source === 'billing'
                          ? 'bg-emerald-900/40 text-emerald-300'
                          : 'bg-blue-900/40 text-blue-300'
                      }`}
                      title={
                        r.source === 'billing'
                          ? 'Derivado del billing real de tu cuenta'
                          : 'Del catálogo público fal.ai/api/models'
                      }
                    >
                      {r.source}
                    </span>
                    {r.source === 'catalog' && r.hasBilling && (
                      <span className="ml-1 text-[9px] text-slate-500" title="También hay billing real">
                        +b
                      </span>
                    )}
                  </td>
                  <td className="py-1 pr-2 text-right text-amber-400 font-medium whitespace-nowrap">
                    ${r.unitPriceUsd.toFixed(4)}
                  </td>
                  <td className="py-1 pr-2 text-slate-400 text-[10px]">{r.unit}</td>
                  <td className="py-1 pr-2 text-right text-slate-500 font-mono text-[10px]">
                    {r.callsCount ?? '—'}
                  </td>
                  <td className="py-1 text-slate-500 text-[10px] whitespace-nowrap">
                    {r.lastSeenAt
                      ? new Date(r.lastSeenAt).toLocaleDateString('es-AR', {
                          month: 'short',
                          day: 'numeric',
                        })
                      : '—'}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Metric({
  label,
  value,
  sublabel,
  color,
}: {
  label: string;
  value: string;
  sublabel?: string;
  color: string;
}) {
  return (
    <div className="bg-slate-900 border border-slate-700 rounded-lg p-3">
      <div className="text-[10px] text-slate-500 uppercase">{label}</div>
      <div className={`text-xl font-semibold ${color} mt-0.5`}>{value}</div>
      {sublabel && <div className="text-[10px] text-slate-500 mt-0.5">{sublabel}</div>}
    </div>
  );
}
